import { Typography, Form, FormInstance, Button, Alert, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	DEFAULT_DATE,
	PredefinedReasonCategory,
	ReviewAction,
} from '../../../shared/Constants';
import { useApproverRecommendationPage } from './useApproverRecommendationPage';
import { InterviewResult } from '../InterviewReportCard/InterviewResult';
import {
	Application,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import {
	canSubmitApproverFeedback,
	isProposerSkippedToEnd,
} from '../../../shared/Utils';
import { RecommendationComponent } from '../RecommendationComponent';
import { ExclamationCircleFilled } from '@ant-design/icons';
import moment from 'moment';
import useUserSession from '../../../hooks/useUserSession';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface ApproverRecommendationCollectionPageProps {
	form: FormInstance;
	application?: Application;
	isResultPublish?: boolean;
	isCareTeamRecommendationDone?: boolean;
}

const SectionHeader = ({
	heading,
	description,
}: {
	heading: string;
	description?: string;
}) => {
	return (
		<div className="flex flex-col mb-[8px]">
			<Text className="font-sans font-bold text-lg text-primary-blue">
				{heading}
			</Text>
			{description && (
				<Text className="mb-[8px]" type="secondary">
					{description}
				</Text>
			)}
		</div>
	);
};

export const ApproverRecommendationCollectionPage = ({
	form,
	application,
	isResultPublish,
	isCareTeamRecommendationDone,
}: ApproverRecommendationCollectionPageProps) => {
	const { t } = useTranslation();
	const { selectedRole } = useUserSession();
	const isApprover = selectedRole === UserRole.APPROVER;

	const canDisplayComments =
		selectedRole !== null &&
		[UserRole.APPROVER, UserRole.SNP_ADMIN, UserRole.SCREENING_ADMIN].includes(
			selectedRole
		);

	const isSkippedToEnd = isProposerSkippedToEnd(
		application?.proposerRecommendation
	);

	return (
		<div className="flex flex-col h-full min-h-0 max-w-[600px]">
			<Form
				name="basic"
				layout="vertical"
				size="middle"
				labelCol={{ span: 16 }}
				style={{ maxWidth: 600 }}
				initialValues={{ remember: true }}
				autoComplete="off"
				form={form}
				disabled={!canSubmitApproverFeedback(application) || !isApprover}
			>
				<SectionHeader heading={t('regionalCommitteeDecision')} />
				<InterviewResult
					application={application}
					finalRecommendationText="overallRecommendation"
				/>
				<div className="flex flex-row items-start">
					<div className="flex flex-col w-full">
						<SectionHeader
							heading={t('regionalCommitteeDecision')}
							description={t('provideRegionalCommitteeDecision') ?? ''}
						/>
						<RecommendationComponent
							commentsMandatory
							commentsLabel={'regionalCommitteeCommentsForAboveRecommendation'}
							commentsPlaceholder="commentsAreMandatory"
							form={form}
							category={
								isSkippedToEnd
									? PredefinedReasonCategory.SKIP
									: PredefinedReasonCategory.APPROVER_REJECT
							}
							displayComments={canDisplayComments}
							disableReview={!canDisplayComments}
							disabled={
								!isApprover || isResultPublish || isCareTeamRecommendationDone
							}
							application={application}
							recommendationBy={UserRole.APPROVER}
						/>
					</div>
				</div>
			</Form>
		</div>
	);
};

export const ApproverRecommendationCollectionPageContainer = () => {
	const { t } = useTranslation();
	const {
		form,
		onSubmitClick,
		onCancelClick,
		application,
		submitButtonDisabled,
	} = useApproverRecommendationPage();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const { currentActiveBatch, selectedRole } = useUserSession();
	const isApprover = selectedRole === UserRole.APPROVER;

	const isResultPublish =
		currentActiveBatch?.snpResultDateForCandidate &&
		currentActiveBatch.snpResultDateForCandidate !== DEFAULT_DATE
			? moment().isSameOrAfter(
					moment(currentActiveBatch.snpResultDateForCandidate)
			  )
			: moment().isSameOrAfter(
					moment(currentActiveBatch?.approverReviewEndDate)
			  );

	const careTeamRecommendation = application?.careTeamRecommendation;
	const approverRecommendation = application?.approverRecommendation;
	const isCareTeamRecommendationDone =
		careTeamRecommendation?.review === ReviewAction.REJECT &&
		approverRecommendation &&
		Object.keys(approverRecommendation).length > 0;

	return (
		<div className="flex justify-center bg-bground-blue">
			<div
				className={`flex flex-col h-full min-h-0 rounded px-5 py-4 ${
					isMobile ? 'w-full' : 'w-2/5 border border-solid border-gray-200'
				}`}
			>
				<ApproverRecommendationCollectionPage
					form={form}
					application={application}
					isResultPublish={isResultPublish}
					isCareTeamRecommendationDone={isCareTeamRecommendationDone}
				/>
				<div className="flex flex-row p-[8px] justify-center">
					<Button onClick={onCancelClick}>{t('cancel')}</Button>
					<Button
						onClick={onSubmitClick}
						type="primary"
						disabled={
							isResultPublish ||
							!isApprover ||
							submitButtonDisabled ||
							isCareTeamRecommendationDone
						}
						className="ml-[16px]"
					>
						{t('submit')}
					</Button>
				</div>
				{!application?.isApprover && (
					<Alert
						message={
							<div className="text-center w-full">
								<ExclamationCircleFilled className="text-warning" />{' '}
								{t('approversAreAllowed')}
							</div>
						}
						type="warning"
					/>
				)}
			</div>
		</div>
	);
};
